/* eslint-disable react/jsx-props-no-spreading */
import { Controller } from 'react-hook-form';

// Styles
import styles from './BatchActionSelect.module.css';

// Utils
import cn from '../../../utils/cn';

// Components
import SelectField from '../../atoms/Select/Select';
import groupOptionsByOptGroup from '../../../utils/optGroup/optGroup';

/**
 * BatchActionSelect component - Custom select for table column actions with groups
 * @param {object} props
 * @param {TransformedBatchActionOption[]} props.groups - Array of option groups
 * @param {(value: Partial<TransformedBatchActionOption>) => void} props.onChange - Function to handle select value change (for updating other fields like lib and type)
 * @param {string} [props.placeholder] - Placeholder text when no value
 * @param {string} [props.name] - Input name attribute
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.disabled] - Whether the component is disabled
 * @param {string} [props.error] - Validation error object
 * @param {import('react-hook-form').Control<any>} [props.control] - React Hook Form control (required if using Controller)
 * @returns {JSX.Element} BatchActionSelect component
 */
function BatchActionSelect({
  groups = [],
  onChange,
  placeholder = '',
  name,
  className = '',
  disabled = false,
  error = null,
  control,
}) {
  /**
   * Handle select change
   * @param {string} selectedValue
   */
  const handleSelectChange = (selectedValue) => {
    const selectedOption = groups.find((option) => option.id === selectedValue);
    if (selectedOption) {
      onChange(selectedOption);
    }
  };

  const options = groupOptionsByOptGroup(groups).map((group) => ({
    label: group.lib,
    options: group.options.map((option) => ({
      value: option.id,
      label: option.lib,
    })),
  }));

  return (
    <div className={cn([styles.wrapper, className])}>
      <div className={cn([styles.group, disabled && styles['group-disabled']])}>
        <Controller
          control={control}
          name={name}
          render={({
            field: {
              ref, onChange: controllerOnChange, value: controllerValue, ...fieldProps
            },
          }) => (
            <SelectField
              {...fieldProps}
              id={name}
              value={controllerValue || null}
              options={options}
              placeholder={placeholder}
              onChange={(newValue) => {
                controllerOnChange(newValue?.value || '');
                if (newValue?.value) {
                  handleSelectChange(newValue.value);
                }
              }}
              error={error}
              disabled={disabled}
            />
          )}
        />
      </div>
    </div>
  );
}

export default BatchActionSelect;
